import React, { useState } from "react";
import trans, { formatDateTime } from "../../../utils";
import PopupBodyItem from "../../Helpers/Popup/PopupParts/PopupBodyItem";
import { Tag, Card, Segmented, Timeline, Button, Image } from "antd";
import {
  DownloadOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ToolOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import { formatDateString } from "../../../JsHelpers/DateFormat";
import useIsMobile from "../../../hooks/useIsMobile";

const ViewSnagBody = ({ data, snagId }) => {
  const isMobile = useIsMobile();
  const [activeOption, setActiveOption] = useState("details");

  const getFileIconUrl = (type = "") => {
    const lowerType = type.toLowerCase();
    if (lowerType === "image/png") {
      return `${process.env.PUBLIC_URL}/assets/PNG.svg`;
    } else if (lowerType === "image/jpg" || lowerType === "image/jpeg") {
      return `${process.env.PUBLIC_URL}/assets/JPG.svg`;
    } else if (lowerType === "application/pdf") {
      return `${process.env.PUBLIC_URL}/assets/PDF.svg`;
    } else {
      return `${process.env.PUBLIC_URL}/assets/file.svg`;
    }
  };

  // ✅ NEW: Check if file is an image
  const isImageFile = (mimetype) => {
    return mimetype?.toLowerCase().startsWith("image/");
  };

  // ✅ NEW: Get file icon component based on mimetype
  const getFileIcon = (mimetype) => {
    const type = mimetype?.toLowerCase();
    if (type?.includes("pdf")) {
      return <FilePdfOutlined style={{ fontSize: "24px", color: "#ff4d4f" }} />;
    } else if (type?.includes("excel") || type?.includes("spreadsheet")) {
      return (
        <FileExcelOutlined style={{ fontSize: "24px", color: "#52c41a" }} />
      );
    } else if (type?.includes("word") || type?.includes("document")) {
      return (
        <FileWordOutlined style={{ fontSize: "24px", color: "#1890ff" }} />
      );
    } else if (type?.includes("image")) {
      return (
        <FileImageOutlined style={{ fontSize: "24px", color: "#722ed1" }} />
      );
    } else {
      return (
        <FileTextOutlined style={{ fontSize: "24px", color: "#8c8c8c" }} />
      );
    }
  };

  // ✅ NEW: Format file size from bytes to MB
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 MB";
    const mb = bytes / (1024 * 1024);
    return mb < 0.01 ? "< 0.01 MB" : `${mb.toFixed(2)} MB`;
  };

  const getActionIcon = (action) => {
    const actionUpper = action?.toUpperCase();
    const iconStyle = (color) => ({
      color,
      backgroundColor: `${color}20`,
      borderRadius: "50%",
      padding: "6px",
      fontSize: "16px",
    });

    if (actionUpper?.includes("CREATED")) {
      return <ClockCircleOutlined style={iconStyle("#1890ff")} />;
    } else if (actionUpper?.includes("FIX")) {
      return <ToolOutlined style={iconStyle("#fa8c16")} />;
    } else if (actionUpper?.includes("RECTIFY")) {
      return <ToolOutlined style={iconStyle("#722ed1")} />;
    } else if (actionUpper?.includes("VERIFY")) {
      return <CheckCircleOutlined style={iconStyle("#52c41a")} />;
    } else if (actionUpper?.includes("REOPEN")) {
      return <SyncOutlined style={iconStyle("#ff4d4f")} />;
    } else if (actionUpper?.includes("DISPUTE")) {
      return <ExclamationCircleOutlined style={iconStyle("#faad14")} />;
    } else if (actionUpper?.includes("CLOSED")) {
      return <CheckCircleOutlined style={iconStyle("#52c41a")} />;
    }

    switch (actionUpper) {
      case "CREATED":
        return <ClockCircleOutlined style={iconStyle("#1890ff")} />;
      case "RECTIFIED":
        return <ToolOutlined style={iconStyle("#52c41a")} />;
      case "QA_VERIFIED":
      case "VERIFIED":
        return <CheckCircleOutlined style={iconStyle("#52c41a")} />;
      case "QA_REJECTED":
      case "CONSULTANT_REJECTED":
      case "REJECTED":
        return <CloseCircleOutlined style={iconStyle("#722ed1")} />;
      case "CONSULTANT_APPROVED":
      case "APPROVED":
        return <CheckCircleOutlined style={iconStyle("#52c41a")} />;
      default:
        return <ClockCircleOutlined style={iconStyle("#1890ff")} />;
    }
  };

  const getActionColor = (action) => {
    const actionUpper = action?.toUpperCase();

    if (actionUpper?.includes("CREATED")) return "blue";
    if (actionUpper?.includes("FIX")) return "orange";
    if (actionUpper?.includes("RECTIFY")) return "purple";
    if (actionUpper?.includes("VERIFY")) return "green";
    if (actionUpper?.includes("REOPEN")) return "red";
    if (actionUpper?.includes("DISPUTE")) return "gold";
    if (actionUpper?.includes("CLOSED")) return "green";

    switch (actionUpper) {
      case "CREATED":
        return "blue";
      case "RECTIFIED":
        return "green";
      case "QA_VERIFIED":
      case "VERIFIED":
        return "green";
      case "QA_REJECTED":
      case "CONSULTANT_REJECTED":
      case "REJECTED":
        return "purple";
      case "CONSULTANT_APPROVED":
      case "APPROVED":
        return "green";
      default:
        return "blue";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "OPEN":
        return "blue";
      case "FIX":
        return "orange";
      case "RECTIFY":
        return "purple";
      case "VERIFY":
        return "green";
      case "REOPEN":
        return "red";
      case "IN_DISPUTE":
        return "gold";
      case "CLOSED":
        return "default";
      default:
        return "blue";
    }
  };

  const downloadAllAttachments = async () => {
    if (!data?.attachments || data.attachments.length === 0) return;

    for (let i = 0; i < data.attachments.length; i++) {
      const file = data.attachments[i];
      setTimeout(() => {
        const link = document.createElement("a");
        link.href = file?.url;
        link.download =
          file?.originalName ||
          file?.name ||
          file?.filename ||
          `attachment-${i + 1}`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, i * 200);
    }
  };

  if (!data) return null;

  const history = data?.history || [];

  return (
    <>
      <Segmented
        options={[
          { label: trans("Snag Details"), value: "details" },
          { label: trans("History"), value: "history" },
        ]}
        style={{ textTransform: "capitalize", marginBottom: "16px" }}
        onChange={(value) => setActiveOption(value)}
        value={activeOption}
        size="large"
        block
      />

      {activeOption === "details" && (
        <>
          <p className="fs-16 fw-500 brand-700 text-uppercase">
            {trans("Snag Details")}
          </p>
          <div className="row">
            <div className="col-lg-6">
              <div className="display-border">
                <PopupBodyItem
                  title={trans("Snag Number")}
                  item={data?.snag_number || "-"}
                />
                <PopupBodyItem
                  title={trans("Snag Type")}
                  item={
                    data?.snag_type ? (
                      <Tag
                        color={
                          data.snag_type === "INTERNAL" ? "blue" : "purple"
                        }
                      >
                        {trans(data.snag_type)}
                      </Tag>
                    ) : (
                      "-"
                    )
                  }
                />
                <PopupBodyItem
                  title={trans("Project")}
                  item={data?.project?.name || "-"}
                />
                <PopupBodyItem
                  title={trans("Zone")}
                  item={data?.zone?.name || "-"}
                />
                <PopupBodyItem
                  title={trans("Level")}
                  item={data?.level?.name || data?.level || "-"}
                />
                {data?.room && (
                  <PopupBodyItem
                    title={trans("Room")}
                    item={data?.room?.name || "-"}
                  />
                )}
                {data?.assignedTo && (
                  <PopupBodyItem
                    title={trans("Assigned To")}
                    item={data?.assignedTo?.name || "-"}
                  />
                )}
              </div>
            </div>
            <div className="col-lg-6">
              <div className="display-border">
                <PopupBodyItem
                  title={trans("Status")}
                  item={
                    data?.status ? (
                      <Tag
                        color={getStatusColor(data.status)}
                        className="text-capitalize"
                      >
                        {data.status_info?.label || trans(data.status)}
                      </Tag>
                    ) : (
                      "-"
                    )
                  }
                />
                <PopupBodyItem
                  title={trans("Reported At")}
                  item={
                    data?.reported_at && formatDateString(data?.reported_at)
                  }
                />
                <PopupBodyItem
                  title={trans("Reported By")}
                  item={data?.reportedBy?.name || "-"}
                />
                <PopupBodyItem
                  title={trans("Sector")}
                  item={data?.sector_label || data?.sector || "-"}
                />
                <PopupBodyItem
                  title={trans("Subsystem")}
                  item={data?.subsystem_label || data?.subsystem || "-"}
                />
                <PopupBodyItem
                  title={trans("Discipline")}
                  item={data?.discipline_label || data?.discipline || "-"}
                />
                {/* <PopupBodyItem
                  title={trans("Defect")}
                  item={data?.item_label || data?.item || "-"}
                /> */}
              </div>
            </div>
            <p className="fs-16 fw-500 brand-700 text-uppercase mt-3">
              {trans("Technical Details")}
            </p>
            <div className="col-lg-12">
              <div className="display-border">
                <PopupBodyItem
                  title={trans("Description")}
                  item={data?.description || "-"}
                />
                <PopupBodyItem
                  title={trans("Severity")}
                  item={
                    data?.severity ? (
                      <Tag
                        color={
                          data.severity === "CRITICAL"
                            ? "red"
                            : data.severity === "MAJOR"
                              ? "orange"
                              : "default"
                        }
                        className="text-capitalize"
                      >
                        {trans(data.severity)}
                      </Tag>
                    ) : (
                      "-"
                    )
                  }
                />
                <PopupBodyItem
                  title={trans("Target Date")}
                  item={
                    data?.target_date && formatDateString(data?.target_date)
                  }
                />
                <PopupBodyItem
                  title={trans("Location Details")}
                  item={data?.location_details || "-"}
                />
              </div>
            </div>
            {data?.attachments?.length > 0 && (
              <div className="col-lg-12 mt-3">
                <Card className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <FileTextOutlined style={{ color: "green" }} />
                      <h5 className="mb-0">{trans("Attachments")}</h5>
                    </div>
                    {!isMobile && (
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadAllAttachments}
                        size="middle"
                      >
                        {trans("Download All")}
                      </Button>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {data.attachments.map((file, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-between align-items-center border p-3 rounded pointer"
                        onClick={() => window.open(`${file?.url}`, "_blank")}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <img
                            src={getFileIconUrl(file?.mimetype)}
                            alt={file?.mimeType || file?.type || "file"}
                            width={28}
                          />
                          <div>
                            {file?.filename ||
                              `${trans("Attachments")} ${index + 1}`}
                            <br />
                            {file?.size && (
                              <div className="text-muted small">
                                {file.size} MB
                              </div>
                            )}
                            {file?.uploadedBy && (
                              <div className="text-muted small">
                                {trans("Uploaded by")}{" "}
                                <span className="fw-800">
                                  {file.uploadedBy?.name}
                                </span>{" "}
                                {trans("on")}{" "}
                                <span className="fw-800">
                                  {formatDateString(file.uploaded_at)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <DownloadOutlined
                          className="text-secondary pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            const link = document.createElement("a");
                            link.href = `${file?.url}`;
                            link.download =
                              file?.originalName ||
                              file?.name ||
                              file?.filename ||
                              `attachment-${index + 1}`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}
          </div>
        </>
      )}

      {activeOption === "history" && (
        <div className="w-100">
          <div className="mb-4">
            <h6 className="fs-16 fw-700 brand-700">{trans("Snag History")}</h6>
          </div>
          {history.length > 0 ? (
            <Timeline
              items={history.map((item, index) => ({
                key: item.id || index,
                dot: getActionIcon(item.action || item.action_label),
                color: getActionColor(item.action || item.action_label),
                children: (
                  <div
                    className={`gap-3 ${isMobile ? "flex-column-1" : "flex-row"}`}
                  >
                    {/* ✅ LEFT SIDE: Main Content */}
                    <div className="flex-grow-1 d-flex flex-column gap-2">
                      {/* Action and Time */}
                      <div className="fs-6 fw-600">
                        {trans(item.action_label || item.action || "Unknown")}
                      </div>
                      <div className="text-secondary fs-6">
                        {formatDateTime(item.changed_at)}
                      </div>

                      {/* Comments */}
                      {item.comments && (
                        <div className="fs-6 mt-1" style={{ color: "#595959" }}>
                          {item.comments}
                        </div>
                      )}

                      {/* Changed By */}
                      {item.changedBy && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted fs-6">
                            {trans("By")}:{" "}
                            {item.changedBy.name || item.changedBy.email}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* ✅ RIGHT SIDE: Attachments (Inline) */}
                    {item.attachments && item.attachments.length > 0 && (
                      <div
                        style={{
                          minWidth: "200px",
                          maxWidth: "300px",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <FileTextOutlined
                            style={{ color: "#1890ff", fontSize: "12px" }}
                          />
                          <span className="fw-600" style={{ fontSize: "12px" }}>
                            {trans("Attachments")} ({item.attachments.length})
                          </span>
                        </div>

                        {/* Images Grid */}
                        {item.attachments.some((file) =>
                          isImageFile(file.mimetype),
                        ) && (
                          <div
                            className="mb-2"
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, 1fr)",
                              gap: "6px",
                            }}
                          >
                            <Image.PreviewGroup>
                              {item.attachments
                                .filter((file) => isImageFile(file.mimetype))
                                .map((file, fileIndex) => (
                                  <Image
                                    key={fileIndex}
                                    src={file.url}
                                    alt={file.original_name || file.filename}
                                    style={{
                                      width: "100%",
                                      height: "80px",
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      border: "1px solid #e8e8e8",
                                    }}
                                  />
                                ))}
                            </Image.PreviewGroup>
                          </div>
                        )}

                        {/* Other Files List - Compact */}
                        {item.attachments.filter(
                          (file) => !isImageFile(file.mimetype),
                        ).length > 0 && (
                          <div className="d-flex flex-column gap-1">
                            {item.attachments
                              .filter((file) => !isImageFile(file.mimetype))
                              .map((file, fileIndex) => (
                                <div
                                  key={fileIndex}
                                  className="d-flex justify-content-between align-items-center p-2 border rounded"
                                  style={{
                                    backgroundColor: "#fafafa",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#f0f0f0";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#fafafa";
                                  }}
                                  onClick={() =>
                                    window.open(file.url, "_blank")
                                  }
                                >
                                  <div
                                    className="d-flex align-items-center gap-2"
                                    style={{ flex: 1, minWidth: 0 }}
                                  >
                                    {getFileIcon(file.mimetype)}
                                    <div
                                      style={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: "500",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                        title={
                                          file.original_name || file.filename
                                        }
                                      >
                                        {file.original_name || file.filename}
                                      </div>
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: "10px" }}
                                      >
                                        {formatFileSize(file.size)}
                                      </div>
                                    </div>
                                  </div>
                                  <DownloadOutlined
                                    className="text-secondary"
                                    style={{ fontSize: "14px", flexShrink: 0 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const link = document.createElement("a");
                                      link.href = file.url;
                                      link.download =
                                        file.original_name || file.filename;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  />
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
          ) : (
            <div className="text-center text-muted py-4">
              {trans("No history available")}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ViewSnagBody;
